import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDeleteListingDraft, useMyDraftListings } from '@steelbuy/data-access';
import { useModal } from '@steelbuy/modal-dialog';
import { ModelPrimaryKey } from '@steelbuy/ts-shared';
import { DeleteDraftModal, ListingDraftTeaser } from '@steelbuy/ui-domain';
import {
    FillHeightContainer,
    LoadingSpinner,
    ButtonTertiaryOnLightM,
    Card,
    ViewHeading,
    useSuccessErrorNotification,
    LoadingStatus,
} from '@steelbuy/ui-primitive';
import { MyListingEmptyState } from './MyListingEmptyState';
import { createRouteUrl, RoutePath } from '../../router/Routes';
import { FOOTER_HEIGHT } from '../app-layout/app-footer/AppFooter';

import './MyListings.scss';
import { getScrollTopMainLayout } from '../app-layout/app-main-layout/AppMainLayout';
import { useScrollPositionContext } from '../scroll-position/ScrollPositionContext';

export const MyListingsDrafts = () => {
    const { t } = useTranslation(['translation', 'uiDomain']);
    const navigate = useNavigate();
    const { setScrollPosition } = useScrollPositionContext();
    const {
        data: drafts,
        isLoading,
        hasNextPage,
        fetchNextPage,
        isFetching,
        total,
        refetch,
        isRefetching,
    } = useMyDraftListings();
    const { mutate } = useDeleteListingDraft();
    const modalAccessor = useModal();
    const [selectedDraftId, setSelectedDraftId] = useState<ModelPrimaryKey>();

    const { Notification, showSuccessNotification, showErrorNotification, hideNotification } =
        useSuccessErrorNotification();

    useEffect(
        () => () => {
            hideNotification();
        },
        []
    );
    const editNavigateState = {
        goBackLink: RoutePath.MY_LISTINGS_DRAFTS,
        goBackText: t('application.myListings.drafts'),
    };

    const handleEditDraft = (draftId: string) => {
        navigate(
            createRouteUrl(
                RoutePath.CREATE_LISTING_WIZARD_WITH_DRAFT,
                ['draftId', draftId],
                ['listingType', 'listing']
            ),
            {
                state: editNavigateState,
            }
        );
    };

    const handleDeleteDraft = (draftId: string) => {
        modalAccessor.show();
        setSelectedDraftId(draftId);
    };

    const handleListingDraftDelete = (draftId: string) => {
        hideNotification();
        modalAccessor.setActionPending(true);
        mutate(draftId, {
            onSuccess: () => {
                showSuccessNotification(t('application.createListingOverview.notificationSuccess'));
                modalAccessor.hide();
                modalAccessor.setActionPending(false);
                refetch();
            },
            onError: () => {
                showErrorNotification(t('application.createListingOverview.notificationError'));
                modalAccessor.hide();
                modalAccessor.setActionPending(false);
            },
        });
    };

    return (
        <div className="my-listings-drafts">
            {isLoading || isRefetching ? (
                <FillHeightContainer extraPadding={FOOTER_HEIGHT}>
                    <LoadingSpinner fillContainerHeight />
                </FillHeightContainer>
            ) : (
                <>
                    {Notification}
                    <ViewHeading value={t('application.myListings.numberOfDrafts', { count: total })} />
                    <div className="my-listings-drafts__listings">
                        {drafts?.map((draft) => (
                            <Card
                                key={draft.id}
                                isClickable
                                url={createRouteUrl(
                                    RoutePath.CREATE_LISTING_WIZARD_WITH_DRAFT,
                                    ['draftId', draft.id],
                                    ['listingType', 'listing']
                                )}
                                linkState={editNavigateState}
                                onClick={() => {
                                    setScrollPosition(RoutePath.MY_LISTINGS_DRAFTS, getScrollTopMainLayout());
                                }}
                            >
                                <ListingDraftTeaser
                                    draft={draft}
                                    onEdit={() => handleEditDraft(draft.id)}
                                    onDelete={() => handleDeleteDraft(draft.id)}
                                />
                            </Card>
                        ))}
                    </div>
                    {hasNextPage && (
                        <div className="my-listings-drafts__load-more">
                            <ButtonTertiaryOnLightM
                                onClick={() => fetchNextPage()}
                                label={t('uiDomain:commonList.loadMore')}
                                loadingStatus={isFetching ? LoadingStatus.PENDING : LoadingStatus.IDLE}
                            />
                        </div>
                    )}
                </>
            )}
            {total === 0 && <MyListingEmptyState navigateTo={RoutePath.CREATE_BULK_LISTING_WIZARD} />}
            <DeleteDraftModal
                modalAccessor={modalAccessor}
                onConfirm={() => {
                    selectedDraftId && handleListingDraftDelete(selectedDraftId);
                }}
            />
        </div>
    );
};
